import { useEffect } from 'react';
import { applyTwitterWidgetStyles } from '@utils/loadTwitterWidget';

const TWITTER_EMBED_PATTERN =
  /(twitter-tweet|x-embed|https?:\/\/(x\.com|twitter\.com)\/\w+\/status\/\d+)/i;

const hasTwitterEmbedCandidate = (value: unknown) => {
  if (typeof value !== 'string') return false;
  return TWITTER_EMBED_PATTERN.test(value);
};

const updateTwitterBlockquoteWidth = () => {
  const blocks = document.querySelectorAll(
    '.x-embed .twitter-tweet, .ql-editor .twitter-tweet',
  );

  blocks.forEach((block) => {
    const element = block as HTMLElement;
    const parent = element.closest(
      '.x-embed, .ql-editor, [class*="DetailContents"]',
    ) as HTMLElement | null;

    const baseWidth = parent?.clientWidth || element.parentElement?.clientWidth || 0;
    if (!baseWidth) return;

    const resolvedWidth = Math.max(280, Math.min(baseWidth - 16, 550));
    element.setAttribute('data-width', String(Math.floor(resolvedWidth)));
  });
};

export function useTwttrLoad(value) {
  useEffect(() => {
    if (value == null || !hasTwitterEmbedCandidate(value)) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isCancelled = false;

    const runWidgetLoad = () => {
      if (isCancelled) return;
      updateTwitterBlockquoteWidth();
      window.twttr.widgets.load();
      setTimeout(() => {
        if (isCancelled) return;
        applyTwitterWidgetStyles();
      }, 250);
    };

    // 트윗이 포함된 콘텐츠가 렌더된 후에 실행
    if (window.twttr?.widgets) {
      timeoutId = setTimeout(runWidgetLoad, 120);
    } else {
      // 기존 스크립트 확인 후 로드
      const existingScript = document.querySelector(
        'script[src="https://platform.twitter.com/widgets.js"]',
      );
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://platform.twitter.com/widgets.js';
        script.async = true;
        script.charset = 'utf-8';
        script.onload = () => {
          let retryCount = 0;
          const maxRetry = 30;
          const waitForAPI = () => {
            if (isCancelled) return;
            if (window.twttr?.widgets) {
              runWidgetLoad();
            } else {
              retryCount += 1;
              if (retryCount <= maxRetry) {
                setTimeout(waitForAPI, 100);
              }
            }
          };
          waitForAPI();
        };
        document.body.appendChild(script);
      } else {
        // 스크립트는 있지만 API가 아직 준비되지 않은 경우
        let retryCount = 0;
        const maxRetry = 30;
        const checkAPI = () => {
          if (isCancelled) return;
          if (window.twttr?.widgets) {
            runWidgetLoad();
          } else {
            retryCount += 1;
            if (retryCount <= maxRetry) {
              setTimeout(checkAPI, 100);
            }
          }
        };
        checkAPI();
      }
    }

    return () => {
      isCancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [value]);
}

export function useTwitterEmbedPaste({
  quillRef,
  value,
  disableTwitterEmbed = false,
}: {
  quillRef: any;
  value?: any;
  disableTwitterEmbed?: boolean;
}) {
  // 트윗 위젯 로더: disableTwitterEmbed가 true이면 로딩 건너뜀
  useTwttrLoad(disableTwitterEmbed ? undefined : value);

  // 전역 플래그만 설정 (붙여넣기 처리 로직은 usePasteFileHandler에서 단일화)
  useEffect(() => {
    (window as any).__disableTwitterEmbed = disableTwitterEmbed;
  }, [disableTwitterEmbed]);
}
